/**
 * Validation Schemas for Insurance Connector
 *
 * Zod schemas for validating form inputs at each state
 */

import {
  insuredStateData,
  storefrontStateData,
} from "@/components/InsuranceStepper/InsuranceStepper.constants";
import { isValid, parse, subYears } from "date-fns";
import { z } from "zod";

/**
 * Servable-state validator — the patient's *residence* ("What state do you
 * live in?").
 *
 * Must resolve to a value in `storefrontStateData` (50 states + DC + PR), the
 * set the shop can actually be configured to serve. Validating membership (not
 * just the `[A-Z]{2}` shape) prevents a persisted-but-no-longer-servable code
 * from rendering as an empty, labelless input while still passing validation
 * and remaining submit-ready.
 */
const servableStateSchema = z
  .string()
  .min(2, "State is required")
  .max(2, "State must be 2 characters")
  .regex(/^[A-Z]{2}$/, "State must be a valid 2-letter code")
  .refine((val) => storefrontStateData.some((s) => s.value === val), {
    message: "Please select a state from the list",
  });

/**
 * Insured-state validator — the insurance *policyholder address*.
 *
 * This is a real mailing address and may be in a US territory (e.g. Guam), so
 * it validates against the full `insuredStateData` set rather than the trimmed
 * shop-servable list. Using the shop list here would wrongly reject a valid
 * persisted territory address at submission.
 */
const insuredStateFieldSchema = z
  .string()
  .min(2, "State is required")
  .max(2, "State must be 2 characters")
  .regex(/^[A-Z]{2}$/, "State must be a valid 2-letter code")
  .refine((val) => insuredStateData.some((s) => s.value === val), {
    message: "Please select a state from the list",
  });

/**
 * Name Input Schema
 */
export const nameSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, "First name is required")
    .max(50, "First name must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "First name contains invalid characters")
    .refine((val) => /[a-zA-Z]/.test(val), {
      message: "First name must contain at least one letter",
    }),
  lastName: z
    .string()
    .trim()
    .min(1, "Last name is required")
    .max(50, "Last name must be less than 50 characters")
    .regex(/^[a-zA-Z\s'-]+$/, "Last name contains invalid characters")
    .refine((val) => /[a-zA-Z]/.test(val), {
      message: "Last name must contain at least one letter",
    }),
});

export type NameFormData = z.infer<typeof nameSchema>;

/**
 * Birthday Input Schema
 */
export const birthdaySchema = z.object({
  birthday: z
    .string()
    .min(1, "Birthday is required")
    .regex(/^\d{2}\s*\/\s*\d{2}\s*\/\s*\d{4}$/, "Format must be MM/DD/YYYY")
    .refine(
      (date) => {
        const cleanedDate = date.replace(/\s/g, "");
        const parsed = parse(cleanedDate, "MM/dd/yyyy", new Date());
        return isValid(parsed);
      },
      { message: "Invalid date" },
    )
    .refine(
      (date) => {
        const cleanedDate = date.replace(/\s/g, "");
        const parsed = parse(cleanedDate, "MM/dd/yyyy", new Date());
        return parsed <= new Date();
      },
      { message: "Birthday cannot be in the future" },
    )
    // Reject implausible years (e.g. a typo like 1299) before they reach the
    // EHR, which rejects them with a 400 that surfaces as 502 noise in error
    // tracking. Mirrors the kiosk DOBInput's 120-year floor (PLA-2632).
    .refine(
      (date) => {
        const cleanedDate = date.replace(/\s/g, "");
        const parsed = parse(cleanedDate, "MM/dd/yyyy", new Date());
        return parsed >= subYears(new Date(), 120);
      },
      { message: "Please enter a valid date of birth" },
    ),
});

export type BirthdayFormData = z.infer<typeof birthdaySchema>;

/**
 * Phone Number Schema
 */
export const phoneNumberSchema = z.object({
  phoneNumber: z
    .string()
    .min(1, "Phone number is required")
    .regex(
      /^\+?1?\s*\(?(\d{3})\)?[\s.-]?(\d{3})[\s.-]?(\d{4})$/,
      "Invalid phone number format",
    ),
});

export type PhoneNumberFormData = z.infer<typeof phoneNumberSchema>;

/**
 * State Selection Schema
 */
export const stateSchema = z.object({
  consumerState: servableStateSchema,
});

export type StateFormData = z.infer<typeof stateSchema>;

/**
 * Insurance Payer Schema
 */
export const insurancePayerSchema = z.object({
  payer: z.string().min(1, "Insurance payer is required"),
  payerId: z.string().optional(),
});

export type InsurancePayerFormData = z.infer<typeof insurancePayerSchema>;

/**
 * Member ID Schema
 */
export const memberIdSchema = z.object({
  memberId: z
    .string()
    .min(1, "Member ID is required")
    .max(50, "Member ID must be less than 50 characters")
    .regex(/^[a-zA-Z0-9-]+$/, "Member ID contains invalid characters"),
});

export type MemberIdFormData = z.infer<typeof memberIdSchema>;

/**
 * Insured State Schema
 */
export const insuredStateSchema = z.object({
  insuredState: insuredStateFieldSchema,
});

export type InsuredStateFormData = z.infer<typeof insuredStateSchema>;

/**
 * Primary Insurance Complete Schema
 */
export const primaryInsuranceSchema = z.object({
  payer: z.string().min(1, "Insurance payer is required"),
  payerId: z.string().optional(),
  memberId: z
    .string()
    .min(1, "Member ID is required")
    .max(50, "Member ID must be less than 50 characters"),
  insuredState: insuredStateFieldSchema,
});

export type PrimaryInsuranceFormData = z.infer<typeof primaryInsuranceSchema>;

/**
 * Secondary Insurance Complete Schema
 */
export const secondaryInsuranceSchema = z.object({
  payer: z.string().min(1, "Insurance payer is required"),
  payerId: z.string().optional(),
  memberId: z
    .string()
    .min(1, "Member ID is required")
    .max(50, "Member ID must be less than 50 characters"),
  insuredState: insuredStateFieldSchema,
});

export type SecondaryInsuranceFormData = z.infer<
  typeof secondaryInsuranceSchema
>;

/**
 * Complete Form Schema (all fields)
 */
export const completeFormSchema = z.object({
  firstName: nameSchema.shape.firstName,
  lastName: nameSchema.shape.lastName,
  birthday: birthdaySchema.shape.birthday,
  consumerState: stateSchema.shape.consumerState.optional(),
  primaryInsurance: primaryInsuranceSchema,
  secondaryInsurance: secondaryInsuranceSchema.optional(),
});

export type CompleteFormData = z.infer<typeof completeFormSchema>;
